// src/pages/admin/FeedbackManagement.jsx
import React, { useState, useEffect } from 'react';
import { 
  FiStar, 
  FiMessageSquare, 
  FiUser, 
  FiCalendar, 
  FiThumbsUp, 
  FiThumbsDown,
  FiEye,
  FiFilter,
  FiTrendingUp,
  FiUsers,
  FiAward,
  FiClock,
  FiEdit3
} from 'react-icons/fi';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import feedbackService from '../../services/feedbackService';
import { getStaff } from '../../services/staffService';
import toast from 'react-hot-toast';

const FeedbackManagement = () => {
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedClinician, setSelectedClinician] = useState('all');
  const [showInternalNote, setShowInternalNote] = useState(null);
  const [internalNote, setInternalNote] = useState('');

  useEffect(() => {
    if (currentTenant?.id) {
      loadFeedbackData();
      loadStaffData();
    }
  }, [currentTenant?.id, selectedFilter, selectedClinician]);

  const loadFeedbackData = async () => {
    try {
      setLoading(true);
      
      const filters = {};
      if (selectedFilter !== 'all') {
        filters.rating = parseInt(selectedFilter);
      }
      if (selectedClinician !== 'all') {
        filters.clinicianId = selectedClinician;
      }

      const [feedbackData, statsData] = await Promise.all([
        feedbackService.getAllFeedback(currentTenant.id, filters),
        feedbackService.getFeedbackStats(currentTenant.id)
      ]);

      setFeedback(feedbackData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading feedback:', error);
      toast.error('Failed to load feedback data');
    } finally {
      setLoading(false);
    }
  };

  const loadStaffData = async () => {
    try {
      const staffData = await getStaff(currentTenant.id);
      setStaff(staffData);
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  const handleMarkAsReviewed = async (feedbackId) => {
    try {
      await feedbackService.markAsReviewed(currentTenant.id, feedbackId, user.uid);
      toast.success('Feedback marked as reviewed');
      loadFeedbackData();
    } catch (error) {
      console.error('Error marking as reviewed:', error);
      toast.error('Failed to mark as reviewed');
    }
  };

  const handleAddInternalNote = async (feedbackId) => {
    if (!internalNote.trim()) return;

    try {
      await feedbackService.addInternalNote(currentTenant.id, feedbackId, internalNote, user.uid);
      toast.success('Internal note added');
      setInternalNote('');
      setShowInternalNote(null);
      loadFeedbackData();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    }
  };

  const getStarRating = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <FiStar 
        key={i} 
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
      />
    ));
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-green-600 bg-green-100';
    if (rating >= 3) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading feedback data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Client Feedback Management</h1>
          <p className="text-gray-600">Monitor and manage client feedback to improve service quality</p>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <FiEye className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-900">Admin-Only Access</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Individual ratings and detailed feedback are only visible to administrators. 
                  Use this data to provide constructive feedback and support to your team.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-blue-100">
                  <FiTrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Average Rating</p>
                  <div className="flex items-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {stats.averageRating.toFixed(1)}
                    </p>
                    <div className="flex ml-2">
                      {getStarRating(Math.round(stats.averageRating))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-green-100">
                  <FiMessageSquare className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Feedback</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalFeedback}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-purple-100">
                  <FiThumbsUp className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Recommendation Rate</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.recommendationRate.toFixed(1)}%</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-orange-100">
                  <FiClock className="w-6 h-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Recent (30 days)</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.recentFeedback}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Filters & Actions */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <FiFilter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filters:</span>
              </div>
              
              <select 
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All Ratings</option>
                <option value="5">⭐⭐⭐⭐⭐ Excellent (5)</option>
                <option value="4">⭐⭐⭐⭐ Very Good (4)</option>
                <option value="3">⭐⭐⭐ Good (3)</option>
                <option value="2">⭐⭐ Needs Improvement (2)</option>
                <option value="1">⭐ Poor (1)</option>
              </select>

              <select 
                value={selectedClinician}
                onChange={(e) => setSelectedClinician(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All Clinicians</option>
                {staff.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button className="px-4 py-2 bg-green-100 text-green-700 rounded-md text-sm hover:bg-green-200 flex items-center gap-2">
                <FiAward className="w-4 h-4" />
                Recognition Report
              </button>
              <button className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md text-sm hover:bg-blue-200 flex items-center gap-2">
                <FiUsers className="w-4 h-4" />
                Team Summary
              </button>
            </div>
          </div>
        </div>

        {/* Feedback List */}
        <div className="space-y-6">
          {feedback.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <FiMessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No feedback found</h3>
              <p className="text-gray-600">No feedback matches your current filters.</p>
            </div>
          ) : (
            feedback.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center space-x-4">
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${getRatingColor(item.rating)}`}>
                        {item.rating}/5 Stars
                      </div>
                      <div className="flex">
                        {getStarRating(item.rating)}
                      </div>
                      {item.wouldRecommend && (
                        <div className="flex items-center text-green-600 text-sm">
                          <FiThumbsUp className="w-4 h-4 mr-1" />
                          Would Recommend
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {!item.reviewed && (
                        <button
                          onClick={() => handleMarkAsReviewed(item.id)}
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm hover:bg-blue-200"
                        >
                          Mark as Reviewed
                        </button>
                      )}
                      <button
                        onClick={() => setShowInternalNote(showInternalNote === item.id ? null : item.id)}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
                      >
                        <FiEdit3 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Client & Appointment Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Client</p>
                      <p className="text-sm text-gray-900">
                        {item.clientName || item.client?.name || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-500">{item.clientEmail}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Appointment</p>
                      <p className="text-sm text-gray-900">{item.treatmentType}</p>
                      <p className="text-xs text-gray-500">
                        {item.appointmentDate ? new Date(item.appointmentDate).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Submitted</p>
                      <p className="text-sm text-gray-900">
                        {new Date(item.submittedAt).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(item.submittedAt).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>

                  {/* Feedback Comment */}
                  {item.comment && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-600 mb-2">Client Comments:</p>
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                        <p className="text-gray-700">{item.comment}</p>
                      </div>
                    </div>
                  )}

                  {/* Internal Notes */}
                  {item.internalNotes && item.internalNotes.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-600 mb-2">Internal Notes:</p>
                      <div className="space-y-2">
                        {item.internalNotes.map((note) => (
                          <div key={note.id} className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                            <p className="text-gray-700 text-sm">{note.note}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Added on {new Date(note.addedAt.toDate()).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Internal Note */}
                  {showInternalNote === item.id && (
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium text-gray-600 mb-2">Add Internal Note:</p>
                      <div className="flex space-x-2">
                        <textarea
                          value={internalNote}
                          onChange={(e) => setInternalNote(e.target.value)}
                          placeholder="Add a note for internal use..."
                          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                          rows="3"
                        />
                        <div className="flex flex-col space-y-2">
                          <button
                            onClick={() => handleAddInternalNote(item.id)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                          >
                            Add Note
                          </button>
                          <button
                            onClick={() => {
                              setShowInternalNote(null);
                              setInternalNote('');
                            }}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-400"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status Indicators */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      {item.reviewed && (
                        <span className="flex items-center text-green-600">
                          <FiEye className="w-4 h-4 mr-1" />
                          Reviewed
                        </span>
                      )}
                      <span>Invoice: {item.invoiceId}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackManagement;
